import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Package, Mail, MessageSquare, LogOut, LayoutDashboard, Box, Tag, Send, FileText, Settings, Building2, Star, Inbox } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { useAdminAuth } from "../../context/AdminAuthContext";

const AdminLayout = () => {
  const { admin, logout, authHeaders } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/stats`, { headers: authHeaders })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, [authHeaders]);

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const links = [
    { to: "/admin", label: "Overzicht", icon: LayoutDashboard, end: true },
    { to: "/admin/requests", label: "Aanvragen", icon: Inbox },
    { to: "/admin/orders", label: "Bestellingen", icon: Package },
    { to: "/admin/invoices", label: "Facturen", icon: FileText },
    { to: "/admin/b2b-invoices", label: "Zakelijke facturen", icon: Building2 },
    { to: "/admin/products", label: "Producten", icon: Box },
    { to: "/admin/discounts", label: "Kortingscodes", icon: Tag },
    { to: "/admin/newsletter", label: "Aanmeldingen", icon: Mail },
    { to: "/admin/campaigns", label: "Nieuwsbrief versturen", icon: Send },
    { to: "/admin/contacts", label: "Contactberichten", icon: MessageSquare },
    { to: "/admin/reviews", label: "Reviews", icon: Star },
    { to: "/admin/settings", label: "Instellingen", icon: Settings },
  ];

  return (
    <div data-testid="admin-layout" className="min-h-screen relative">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              ADMIN PANEEL
            </div>
            <div className="font-display text-4xl sm:text-5xl mt-2">
              HET <span className="neon-red-text">CONTROLEKAMER.</span>
            </div>
            <div className="mt-2 text-xs font-mono-x text-[var(--tha-muted)]">
              Ingelogd als <span className="text-white">{admin?.email}</span>
            </div>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={doLogout}
            className="btn-ghost-red px-4 py-2.5 inline-flex items-center gap-2 text-sm"
          >
            <LogOut size={14} /> UITLOGGEN
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-[var(--tha-border)] mb-10">
          <StatCard label="AANVRAGEN OPEN" value={stats?.open_requests ?? "—"} accent />
          <StatCard label="BESTELLINGEN" value={stats?.orders ?? "—"} />
          <StatCard label="NIEUWSBRIEF" value={stats?.newsletter ?? "—"} />
          <StatCard label="VOORRAAD" value={stats?.stock ?? "—"} />
          <StatCard
            label="OMZET"
            value={stats ? `€${stats.revenue.toFixed(2)}` : "—"}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3">
            <nav className="border border-[var(--tha-border)] bg-[#0a0a0a]">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-4 border-b border-[var(--tha-border)] last:border-0 text-sm font-mono-x tracking-widest uppercase transition-colors ${
                      isActive
                        ? "bg-[#111] text-[var(--tha-red)]"
                        : "text-[var(--tha-muted)] hover:text-white"
                    }`
                  }
                  data-testid={`admin-nav-${l.label.toLowerCase()}`}
                >
                  <l.icon size={14} /> {l.label}
                </NavLink>
              ))}
            </nav>
          </aside>

          <section className="lg:col-span-9">
            <Outlet context={{ authHeaders }} />
          </section>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div className={`bg-[#0a0a0a] p-5 ${accent ? "border-l-2 border-l-[var(--tha-red)]" : ""}`}>
    <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
      {label}
    </div>
    <div className="font-display text-3xl mt-2 text-white">{value}</div>
  </div>
);

export default AdminLayout;
